import React, { useState, useEffect } from 'react';
import { Monitor, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AppHeader, UserMenu } from '@/components/layout/AppHeader';
import ProfessorInterface from './ProfessorInterface';
import ReviewProfessorInterface from './ReviewProfessorInterface';
import ReviewHistoryDialog from './ReviewHistoryDialog';
import ProfessorAuth from './ProfessorAuth';
import { getProfessorToken, clearProfessorToken } from '../authCookie';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ProfessorData {
  professor_netid: string;
  professor_name: string;
  professor_email: string;
}

interface ClassInfo {
  class_id: number;
  class_code: string;
  join_code?: string;
  class_name: string;
  class_period: string;
}

export interface SessionInfo {
  session_id: string;
  class_id: number;
  class_code: string;
  join_code?: string;
  class_name: string;
  class_period: string;
  session_timecreated: string;
  mode?: string;
  expires_at?: string | null;
}

interface PreviousQuestion {
  question_id: string;
  question_text: string;
  votes: number;
  question_timestamp: string;
  session_id: string;
  session_timecreated: string;
}

interface StaffMember {
  professor_netid: string;
  professor_name: string;
  professor_email: string;
  role: string;
}

/** Join-code pill for the header: the code itself and a full-screen display dialog. */
function JoinCodeChip({ joinCode, displayHost }: { joinCode: string; displayHost: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-10 items-center gap-1 rounded-full border bg-background pl-3 pr-1 sm:pl-3.5">
        <span className="hidden text-xs text-muted-foreground sm:inline">Code</span>
        <span className="mr-1 text-sm font-bold tracking-[0.2em]">{joinCode}</span>
        <Dialog>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="hidden rounded-full sm:inline-flex" aria-label="Display join code">
                  <Monitor aria-hidden />
                </Button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent>Show on projector</TooltipContent>
          </Tooltip>
          <DialogContent className="max-w-[92vw] rounded-2xl px-8 py-14 text-center sm:max-w-3xl sm:px-16">
            <DialogHeader className="sr-only">
              <DialogTitle>Join information</DialogTitle>
              <DialogDescription>Share this address and class code with students.</DialogDescription>
            </DialogHeader>
            <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Join at</p>
            <p className="mt-2 break-all text-[clamp(1.25rem,4vw,2.25rem)] font-bold tracking-tight">{displayHost}</p>
            <div className="mx-auto my-10 h-px w-24 bg-border" />
            <p className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Class code</p>
            <p className="mt-2 text-[clamp(3rem,12vw,7rem)] font-extrabold leading-none tracking-[0.15em]">{joinCode}</p>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

/** Owner-facing panel to view and manage a class's teaching fellows. TFs see a
 * read-only roster; the owner can add (by email) and remove co-instructors. */
function ManageStaffDialog({
  professor,
  classId,
  classLabel,
}: {
  professor: ProfessorData;
  classId: number;
  classLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = (): Record<string, string> => {
    const t = getProfessorToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(`${backendUrl}/api/classes/${classId}/staff`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) {
          setStaff(data.staff);
        } else {
          setError(data?.detail || 'Failed to load teaching fellows.');
        }
      } catch {
        if (!cancelled) setError('Unable to load teaching fellows.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, classId]);

  const isOwner = staff.some(
    (m) => m.professor_netid === professor.professor_netid && m.role === 'owner'
  );

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter the teaching fellow\'s email.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/classes/${classId}/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || 'Failed to add teaching fellow.');
        return;
      }
      setStaff(data.staff);
      setEmail('');
    } catch {
      setError('Unable to add teaching fellow.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemove = async (netid: string) => {
    setIsBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `${backendUrl}/api/classes/${classId}/staff/${encodeURIComponent(netid)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || 'Failed to remove teaching fellow.');
        return;
      }
      setStaff(data.staff);
    } catch {
      setError('Unable to remove teaching fellow.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full">
          Manage teaching fellows
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Teaching fellows</DialogTitle>
          <DialogDescription>{classLabel}</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <ul className="divide-y">
            {staff.map((m) => (
              <li key={m.professor_netid} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{m.professor_name}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.professor_email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={m.role === 'owner' ? 'secondary' : 'outline'}>
                    {m.role === 'owner' ? 'Owner' : 'TF'}
                  </Badge>
                  {isOwner && m.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${m.professor_name}`}
                      disabled={isBusy}
                      onClick={() => handleRemove(m.professor_netid)}
                    >
                      <X aria-hidden />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {isOwner ? (
          <form onSubmit={handleAdd} className="flex gap-2">
            <Input
              type="email"
              placeholder="tf@yale.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="h-10"
            />
            <Button type="submit" disabled={isBusy}>Add</Button>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">
            Only the class owner can add or remove teaching fellows.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ActiveSessionSummary {
  session_id: string;
  mode: string;
  session_timecreated: string;
  expires_at?: string | null;
}

function ClassSelector({
  professor,
  onSessionCreated,
  onLogout
}: {
  professor: ProfessorData;
  onSessionCreated: (session: SessionInfo) => void;
  onLogout: () => void;
}) {
  const [classes, setClasses] = useState<ClassInfo[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [openSessions, setOpenSessions] = useState<ActiveSessionSummary[]>([]);
  const [mode, setMode] = useState<'live' | 'review'>('live');
  const [durationDays, setDurationDays] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const token = getProfessorToken();
        const response = await fetch(`${backendUrl}/api/professors/${professor.professor_netid}/classes`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await response.json();
        if (data.success) {
          setClasses(data.classes);
          if (data.classes.length > 0) {
            setSelectedClassId(data.classes[0].class_id);
          }
        } else {
          setError('Failed to load classes');
        }
      } catch (err) {
        console.error('Error fetching classes:', err);
        setError('Unable to load classes. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClasses();
  }, [professor.professor_netid]);

  // Load any already-open sessions for the selected class, so the professor can
  // jump back into one they left (e.g. a multi-day review after logging out).
  useEffect(() => {
    if (selectedClassId == null) {
      setOpenSessions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = getProfessorToken();
        const res = await fetch(`${backendUrl}/api/classes/${selectedClassId}/active-sessions`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!cancelled) setOpenSessions(res.ok && data.success ? data.sessions || [] : []);
      } catch {
        if (!cancelled) setOpenSessions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClassId]);

  const handleResume = (s: ActiveSessionSummary) => {
    const cls = classes.find((c) => c.class_id === selectedClassId);
    if (!cls) return;
    onSessionCreated({
      session_id: s.session_id,
      class_id: cls.class_id,
      class_code: cls.class_code,
      join_code: cls.join_code,
      class_name: cls.class_name,
      class_period: cls.class_period,
      session_timecreated: s.session_timecreated,
      mode: s.mode,
      expires_at: s.expires_at,
    });
  };

  const handleStartSession = async () => {
    if (selectedClassId == null) {
      setError('Please select a class');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const sessionToken = getProfessorToken();
      const response = await fetch(`${backendUrl}/api/sessions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        },
        body: JSON.stringify({
          professor_netid: professor.professor_netid,
          class_id: selectedClassId,
          mode,
          ...(mode === 'review' ? { duration_days: durationDays } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data?.detail || 'Failed to create session');
        return;
      }

      onSessionCreated({
        session_id: data.session_id,
        class_id: data.class_id,
        class_code: data.class_code,
        join_code: data.join_code,
        class_name: data.class_name,
        class_period: data.class_period,
        session_timecreated: data.session_timecreated,
        mode: data.mode,
        expires_at: data.expires_at,
      });
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Unable to create session. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const firstName = professor.professor_name.split(' ')[0] || professor.professor_name;
  const selectedClass = classes.find((c) => c.class_id === selectedClassId) ?? null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader
        right={<UserMenu name={professor.professor_name} email={professor.professor_email} onLogout={onLogout} />}
      />
      <main className="container max-w-lg py-16">
        <h1 className="text-3xl font-bold tracking-tight">Welcome back, {firstName}</h1>
        <p className="mt-2 text-muted-foreground">Pick a course to open a live question feed for today's lecture.</p>

        <div className="mt-8 rounded-2xl border bg-card p-8 shadow-soft">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : classes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No courses are linked to your account yet.</p>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="course-select">Course</Label>
                <Select
                  value={selectedClassId != null ? String(selectedClassId) : undefined}
                  onValueChange={(value) => setSelectedClassId(Number(value))}
                >
                  <SelectTrigger id="course-select" className="h-12 rounded-lg text-base">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.class_id} value={String(cls.class_id)} className="py-2.5">
                        <span className="font-semibold">{cls.class_code}</span>
                        <span className="text-muted-foreground"> · {cls.class_name} · {cls.class_period}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {openSessions.length > 0 && (
                <div className="space-y-2">
                  <Label>Open sessions</Label>
                  <ul className="space-y-2">
                    {openSessions.map((s) => (
                      <li key={s.session_id}>
                        <button
                          type="button"
                          onClick={() => handleResume(s)}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted"
                        >
                          <span className="min-w-0 text-sm">
                            <span className="font-medium">
                              {s.mode === 'review' ? 'Review session' : 'Live lecture'}
                            </span>
                            <span className="text-muted-foreground">
                              {s.mode === 'review' && s.expires_at
                                ? ` · open until ${new Date(s.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                                : ` · started ${new Date(s.session_timecreated).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`}
                            </span>
                          </span>
                          <span className="shrink-0 text-sm font-medium text-brand-text">Resume</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-2">
                <Label>Session type</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={mode === 'live' ? 'default' : 'outline'}
                    onClick={() => setMode('live')}
                  >
                    Live lecture
                  </Button>
                  <Button
                    type="button"
                    variant={mode === 'review' ? 'default' : 'outline'}
                    onClick={() => setMode('review')}
                  >
                    Review session
                  </Button>
                </div>
              </div>

              {mode === 'review' && (
                <div className="space-y-2">
                  <Label htmlFor="duration">Open for (days)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min={1}
                    max={14}
                    value={durationDays}
                    onChange={(e) =>
                      setDurationDays(Math.max(1, Math.min(14, Number(e.target.value) || 1)))
                    }
                    className="h-11 rounded-lg"
                  />
                </div>
              )}

              <Button
                size="lg"
                className="w-full"
                onClick={handleStartSession}
                disabled={isCreating || selectedClassId == null}
              >
                {isCreating ? 'Starting...' : mode === 'review' ? 'Open review session' : 'Start class'}
              </Button>

              {selectedClass && (
                <>
                  <ManageStaffDialog
                    professor={professor}
                    classId={selectedClass.class_id}
                    classLabel={`${selectedClass.class_code} · ${selectedClass.class_name}`}
                  />
                  <ReviewHistoryDialog
                    classId={selectedClass.class_id}
                    classLabel={`${selectedClass.class_code} · ${selectedClass.class_name}`}
                    backendUrl={backendUrl}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function ProfessorPage() {
  const [professor, setProfessor] = useState<ProfessorData | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [previousQuestions, setPreviousQuestions] = useState<PreviousQuestion[]>([]);
  const [sessionCheckDone, setSessionCheckDone] = useState(false);

  useEffect(() => {
    const token = getProfessorToken();
    if (!token) {
      setSessionCheckDone(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          const restoredProf: ProfessorData = {
            professor_netid: data.professor_netid,
            professor_name: data.professor_name,
            professor_email: data.professor_email,
          };
          setProfessor(restoredProf);

          try {
            const sessionRes = await fetch(
              `${backendUrl}/api/professors/${restoredProf.professor_netid}/active-session`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!cancelled && sessionRes.ok) {
              const sData = await sessionRes.json();
              if (sData.has_active_session) {
                handleSessionCreated({
                  session_id: sData.session_id,
                  class_id: sData.class_id,
                  class_code: sData.class_code,
                  join_code: sData.join_code,
                  class_name: sData.class_name,
                  class_period: sData.class_period,
                  session_timecreated: sData.session_timecreated,
                  mode: sData.mode,
                  expires_at: sData.expires_at,
                });
              }
            }
          } catch {
            // Active session check is best-effort
          }
        } else {
          clearProfessorToken();
        }
      } catch {
        if (!cancelled) clearProfessorToken();
      } finally {
        if (!cancelled) setSessionCheckDone(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToHome = () => {
    setSession(null);
    setPreviousQuestions([]);
  };

  const handleLogout = async () => {
    if (session) {
      try {
        await fetch(`${backendUrl}/api/sessions/${session.session_id}/save`, {
          method: 'POST',
        });
      } catch (err) {
        console.error('Error saving session:', err);
      }
    }
    clearProfessorToken();
    setProfessor(null);
    setSession(null);
    setPreviousQuestions([]);
  };

  const profAuthHeaders = (): Record<string, string> => {
    const t = getProfessorToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const handleSessionCreated = async (newSession: SessionInfo) => {
    setSession(newSession);

    try {
      const response = await fetch(
        `${backendUrl}/api/classes/${newSession.class_id}/previous-unanswered?exclude_session=${newSession.session_id}`,
        { headers: profAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setPreviousQuestions(data.questions);
      }
    } catch (err) {
      console.error('Error fetching previous questions:', err);
    }
  };

  const handleStartNewSession = async () => {
    if (!professor || !session) return;

    try {
      const response = await fetch(`${backendUrl}/api/sessions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...profAuthHeaders(),
        },
        body: JSON.stringify({
          professor_netid: professor.professor_netid,
          class_id: session.class_id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Failed to create new session:', data?.detail);
        return;
      }

      const newSession: SessionInfo = {
        session_id: data.session_id,
        class_id: data.class_id,
        class_code: data.class_code,
        join_code: data.join_code,
        class_name: data.class_name,
        class_period: data.class_period,
        session_timecreated: data.session_timecreated,
      };

      setSession(newSession);

      try {
        const prevResponse = await fetch(
          `${backendUrl}/api/classes/${newSession.class_id}/previous-unanswered?exclude_session=${newSession.session_id}`,
          { headers: profAuthHeaders() }
        );
        const prevData = await prevResponse.json();
        if (prevData.success) {
          setPreviousQuestions(prevData.questions);
        }
      } catch (err) {
        console.error('Error fetching previous questions:', err);
      }
    } catch (err) {
      console.error('Error creating new session:', err);
    }
  };

  if (!sessionCheckDone) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <AppHeader />
        <main className="container max-w-3xl py-10">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-3 h-4 w-40" />
          <Skeleton className="mt-10 h-40 w-full rounded-xl" />
        </main>
      </div>
    );
  }
  if (!professor) {
    return (
      <ProfessorAuth
        backendUrl={backendUrl}
        onAuthenticated={(prof) => setProfessor(prof)}
      />
    );
  }

  if (!session) {
    return (
      <ClassSelector
        professor={professor}
        onSessionCreated={handleSessionCreated}
        onLogout={handleLogout}
      />
    );
  }

  const frontendUrl = (process.env.REACT_APP_FRONTEND_URL || 'https://trylectureme.com').replace(/\/$/, '');
  const joinCode = session.join_code ?? session.session_id;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader
        left={
          <div className="hidden min-w-0 items-center gap-3 text-sm sm:flex">
            <span className="h-5 w-px bg-border" aria-hidden />
            <span className="font-semibold">{session.class_code}</span>
            <span className="truncate text-muted-foreground">{session.class_name}</span>
          </div>
        }
        right={
          <>
            <JoinCodeChip
              joinCode={joinCode}
              displayHost={`${frontendUrl.replace(/^https?:\/\//, '')}/student`}
            />
            <UserMenu name={professor.professor_name} email={professor.professor_email} onLogout={handleLogout} />
          </>
        }
      />

      {session.mode === 'review' ? (
        <ReviewProfessorInterface
          sessionId={session.session_id}
          expiresAt={session.expires_at}
          backendUrl={backendUrl}
          onEnded={handleBackToHome}
        />
      ) : (
        <ProfessorInterface
          sessionId={session.session_id}
          classCode={session.class_code}
          className={session.class_name}
          sessionTimecreated={session.session_timecreated}
          backendUrl={backendUrl}
          professorName={professor.professor_name}
          previousQuestions={previousQuestions}
          onStartNewSession={handleStartNewSession}
          onBackToHome={handleBackToHome}
        />
      )}
    </div>
  );
}
